import { CheckCircle, Clock, Warning, XCircle, Circle, CurrencyCircleDollar } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { cn } from '../utils/cn';

/**
 * StatusBadge — Color + icon pairing for operational state (UX Invariant §9, rule 4).
 *
 * Color-only indicators are forbidden: every status MUST also carry a shape/icon
 * so colorblind users (and greyscale printouts) can distinguish them.
 */
export type StatusVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'pending'
  | 'paid';

interface StatusDef {
  className: string;
  icon: ReactNode;
}

const STATUS_MAP: Record<StatusVariant, StatusDef> = {
  success: {
    className: 'bg-biz-success-subtle text-biz-success border-biz-success/30',
    icon: <CheckCircle weight="regular" size={16} aria-hidden="true" />,
  },
  warning: {
    className: 'bg-biz-warning-subtle text-biz-warning border-biz-warning/30',
    icon: <Warning weight="regular" size={16} aria-hidden="true" />,
  },
  danger: {
    className: 'bg-biz-danger-subtle text-biz-danger border-biz-danger/30',
    icon: <XCircle weight="regular" size={16} aria-hidden="true" />,
  },
  info: {
    className: 'bg-biz-info-subtle text-biz-info border-biz-info/30',
    icon: <Circle weight="regular" size={16} aria-hidden="true" />,
  },
  neutral: {
    className: 'bg-biz-muted text-biz-muted-foreground border-biz-border',
    icon: <Circle weight="regular" size={16} aria-hidden="true" />,
  },
  pending: {
    className: 'bg-biz-warning-subtle text-biz-warning border-biz-warning/30',
    icon: <Clock weight="regular" size={16} aria-hidden="true" />,
  },
  paid: {
    className: 'bg-biz-success-subtle text-biz-success border-biz-success/30',
    icon: <CurrencyCircleDollar weight="regular" size={16} aria-hidden="true" />,
  },
};

export function StatusBadge({
  variant,
  label,
  className,
}: {
  variant: StatusVariant;
  label: string;
  className?: string;
}): JSX.Element {
  const def = STATUS_MAP[variant];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        'px-biz-content py-1 min-h-biz-touch-sm',
        'rounded-biz-full border',
        'text-biz-xs font-medium',
        def.className,
        className,
      )}
    >
      {def.icon}
      <span>{label}</span>
    </span>
  );
}
